import { Injectable } from '@angular/core';
import { StateService } from '../../utils';
import { map, take } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from '../components';
import { from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LayoutService extends StateService<State> {
  private defaultSidebartype?: 'full' | 'iconbar' | 'overlay' | 'mini-sidebar';

  boxed$ = this.select((state) => state.boxed);
  collapsable$ = this.select((state) => state.collapsable);
  dir$ = this.select((state) => state.dir);
  expandLogo$ = this.select((state) => state.expandLogo);
  headerpos$ = this.select((state) => state.headerpos);
  isCollapsed$ = this.select((state) => state.isCollapsed);
  layout$ = this.select((state) => state.layout);
  logobg$ = this.select((state) => state.logobg);
  navbarbg$ = this.select((state) => state.navbarbg);
  showMobileMenu$ = this.select((state) => state.showMobileMenu);
  showSettings$ = this.select((state) => state.showSettings);
  sidebarbg$ = this.select((state) => state.sidebarbg);
  sidebarpos$ = this.select((state) => state.sidebarpos);
  sidebartype$ = this.select((state) => state.sidebartype);
  theme$ = this.select((state) => state.theme);

  set boxed(v: string) {
    this.state = { boxed: v };
  }
  set collapsable(v: boolean) {
    this.state = { collapsable: v };
  }
  set dir(v: string) {
    this.state = { dir: v };
  }
  set headerpos(v: string) {
    this.state = { headerpos: v };
  }
  set logobg(v: string) {
    this.state = { logobg: v };
  }
  set navbarbg(v: string) {
    this.state = { navbarbg: v };
  }
  set sidebarbg(v: string) {
    this.state = { sidebarbg: v };
  }
  set sidebarpos(v: string) {
    this.state = { sidebarpos: v };
  }
  set sidebartype(v: 'full' | 'iconbar' | 'overlay' | 'mini-sidebar') {
    this.state = { sidebartype: v };
  }
  set theme(v: string) {
    this.state = { theme: v };
  }

  constructor(private modalSvc: NgbModal) {
    super(initialState);
    this.select((state) => state.sidebartype)
      .pipe(take(1))
      .subscribe((sidebartype) => (this.defaultSidebartype = sidebartype));
    this.select((state) => state.theme).subscribe((theme) => {
      document.body.setAttribute('data-theme', theme);
    });
  }

  confirm(title: string, body: string) {
    const modalRef = this.modalSvc.open(ConfirmationModalComponent, {
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.body = body;
    return from(modalRef.result).pipe(map((result) => result as boolean));
  }

  handleSidebar(): void {
    const innerWidth = window.innerWidth;
    switch (this.defaultSidebartype) {
      case 'full':
      case 'iconbar':
        if (this.state.collapsable) {
          if (innerWidth < 1170) {
            this.state = { sidebartype: 'mini-sidebar' };
          } else {
            this.state = { sidebartype: this.defaultSidebartype };
          }
        }
        break;

      case 'overlay':
        if (innerWidth < 767) {
          this.state = { sidebartype: 'mini-sidebar' };
        } else {
          this.state = { sidebartype: this.defaultSidebartype };
        }
        break;

      default:
    }
  }

  setDarkMode(): void {
    this.state = {
      theme: 'dark',
      logobg: 'skin5',
      navbarbg: 'skin5',
    };
  }

  toggleCollapsed() {
    this.state = {
      isCollapsed: !this.state.isCollapsed,
    };
  }

  toggleDarkMode(): void {
    this.state = {
      theme: this.state.theme === 'light' ? 'dark' : 'light',
      logobg: this.state.theme === 'light' ? 'skin5' : 'skin6',
      navbarbg: this.state.theme === 'light' ? 'skin5' : 'skin6',
    };
  }

  toggleExpandLogo() {
    this.state = {
      expandLogo: !this.state.expandLogo,
    };
  }

  toggleMobileMenu() {
    this.state = {
      showMobileMenu: !this.state.showMobileMenu,
    };
  }

  toggleSettings() {
    this.state = {
      showSettings: !this.state.showSettings,
    };
  }

  toggleSidebarType(): void {
    switch (this.state.sidebartype) {
      case 'full':
      case 'iconbar':
        this.state = { sidebartype: 'mini-sidebar' };
        break;
      case 'overlay':
        this.state = { showMobileMenu: !this.state.showMobileMenu };
        break;

      case 'mini-sidebar':
        if (this.defaultSidebartype === 'mini-sidebar') {
          this.state = { sidebartype: 'full' };
        } else {
          this.state = { sidebartype: this.defaultSidebartype };
        }
        break;

      default:
    }
  }
}

const initialState: State = {
  boxed: 'full',
  collapsable: true,
  dir: 'ltr',
  expandLogo: false,
  headerpos: 'fixed',
  isCollapsed: false,
  layout: 'vertical',
  logobg: 'skin6',
  navbarbg: 'skin6',
  showMobileMenu: false,
  showSettings: false,
  sidebarbg: 'skin5',
  sidebarpos: 'fixed',
  sidebartype: 'full',
  theme: 'light',
};

interface State {
  boxed: string;
  collapsable: boolean;
  dir: string;
  expandLogo: boolean;
  headerpos: string;
  isCollapsed: boolean;
  layout: string;
  logobg: string;
  navbarbg: string;
  showMobileMenu: boolean;
  showSettings: boolean;
  sidebarbg: string;
  sidebarpos: string;
  sidebartype: 'full' | 'iconbar' | 'overlay' | 'mini-sidebar';
  theme: string;
}
